import React from 'react';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import ExportButton from './ExportButton';
import { CalendarEvent, ViewType } from './types';
import { parseDateFlexible } from '@/utils/Date';

// Helper: format a Date to YYYY-MM-DD
const formatDateDay = (d: Date) => d.toISOString().substring(0, 10);

interface CalendarViewProps {
  currentDate: Date;
  view: ViewType;
  setView: (view: ViewType) => void;
  filteredEvents: CalendarEvent[];
  handlePrevious: () => void;
  handleNext: () => void;
  handleToday: () => void;
  handleEventClick: (event: CalendarEvent, date?: Date) => void;
  onMoreClick: (date: Date, events: CalendarEvent[]) => void;
  showStatusLabel?: boolean;
  showAssetTypeLabel?: boolean;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  currentDate,
  view,
  setView,
  filteredEvents,
  handlePrevious,
  handleNext,
  handleToday,
  handleEventClick,
  onMoreClick,
  showStatusLabel = true,
  showAssetTypeLabel = true,
}) => {
  // Responsive: detect small screens and force Day view for readability
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  React.useEffect(() => {
    if (isMobile && view !== 'day') {
      setView('day');
    }
  }, [isMobile, view, setView]);

  const visibleEvents = React.useMemo(() => {
    let viewStart: Date;
    let viewEnd: Date;

    const normalizeDate = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());

    if (view === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      viewStart = new Date(year, month, 1);
      viewEnd = new Date(year, month + 1, 0); // Last day of the exact month
    } else if (view === 'week') {
      viewStart = new Date(currentDate);
      const dayOfWeek = (viewStart.getDay() + 6) % 7;
      viewStart.setDate(viewStart.getDate() - dayOfWeek);
      viewEnd = new Date(viewStart);
      viewEnd.setDate(viewEnd.getDate() + 6);
    } else {
      viewStart = new Date(currentDate);
      viewEnd = new Date(currentDate);
    }

    const viewStartTs = normalizeDate(viewStart).getTime();
    const viewEndTs = normalizeDate(viewEnd).getTime();

    const toNormalizedTs = (value: string): number => {
      const dt = parseDateFlexible(value);
      if (!dt) return NaN;
      return normalizeDate(dt).getTime();
    };

    return filteredEvents
      .map((ev) => {
        const startTs = toNormalizedTs(ev.start);
        const endTs = ev.end ? toNormalizedTs(ev.end) : Number.POSITIVE_INFINITY;

        if (isNaN(startTs)) return null;

        // Check for exact overlap with the strictly selected calendar period
        if (startTs <= viewEndTs && endTs >= viewStartTs) {
          // Clamp start and end dates so the export exactly reflects the selected calendar period
          const clampedStartTs = Math.max(startTs, viewStartTs);
          const clampedEndTs = Math.min(endTs, viewEndTs);
          
          return {
            ...ev,
            start: formatDateDay(new Date(clampedStartTs)),
            end: clampedEndTs === Number.POSITIVE_INFINITY 
              ? ev.end 
              : formatDateDay(new Date(clampedEndTs)),
          };
        }
        return null;
      })
      .filter(Boolean) as CalendarEvent[];
  }, [currentDate, view, filteredEvents]);

  return (
    <div data-testid="calendar-view">
      <div className="calendar-view-header p-1 flex justify-between items-center mb-4">
        <CalendarHeader
          currentDate={currentDate}
          view={view}
          onViewChange={setView}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onToday={handleToday}
        />
        <div className="calendar-toolbar flex items-center gap-3">
          <div className="view-switch flex items-center gap-2">
            {isMobile ? (
              <button
                onClick={() => setView('day')}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${
                    view === 'day'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 border border-gray-400 hover:bg-gray-200'
                  }
                `}
              >
                Day
              </button>
            ) : (
              (['month', 'week', 'day'] as ViewType[]).map((viewType) => (
                <button
                  key={viewType}
                  onClick={() => setView(viewType)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${
                      view === viewType
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 border border-gray-400 hover:bg-gray-200'
                    }
                  `}
                >
                  {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
                </button>
              ))
            )}
          </div>
          <ExportButton
            events={visibleEvents}
            filename={`bookings-${formatDateDay(currentDate)}.csv`}
          />
        </div>
      </div>
      {/* Calendar Grid */}
      <div
        className={`calendar-grid-wrapper bg-gray-100 rounded-2xl shadow-lg border border-gray-100 ${
          view === 'day' ? 'overflow-visible' : 'overflow-hidden'
        }`}
      >
        <CalendarGrid
          currentDate={currentDate}
          view={view}
          events={filteredEvents}
          onEventClick={handleEventClick}
          onMoreClick={onMoreClick}
          showStatusLabel={showStatusLabel}
          showAssetTypeLabel={showAssetTypeLabel}
        />
      </div>
    </div>
  );
};

export default CalendarView;
